'use client';

import Link from 'next/link';
import { useContent } from '@/hooks/use-content';

type BrandLogoProps = {
  variant?: 'dark' | 'light';
  size?: 'sm' | 'md' | 'lg';
  /** Logo already includes "BESHR MAKKI" — wordmark off by default */
  showWordmark?: boolean;
  href?: string;
  className?: string;
};

/** Asset 6 — full lockup (script + BESHR MAKKI) */
const LOGO_LIGHT_BG = '/images/brand/logo-full-dark.png';
const LOGO_DARK_BG = '/images/brand/logo-full.png';

const sizeMap = {
  sm: { img: 'h-11 md:h-12 w-auto max-w-[170px]', text: 'text-sm' },
  md: { img: 'h-[3.5rem] md:h-16 w-auto max-w-[220px] md:max-w-[260px]', text: 'text-base' },
  lg: { img: 'h-16 md:h-[4.75rem] w-auto max-w-[260px] md:max-w-[320px]', text: 'text-lg' },
};

export default function BrandLogo({
  variant = 'dark',
  size = 'md',
  showWordmark = false,
  href = '/',
  className = '',
}: BrandLogoProps) {
  const c = useContent();
  const sizes = sizeMap[size];
  const src = variant === 'dark' ? LOGO_DARK_BG : LOGO_LIGHT_BG;
  const textClass = variant === 'dark' ? 'text-white' : 'text-[var(--bm-charcoal)]';

  const content = (
    <span className={`inline-flex items-center gap-2.5 md:gap-3 ${className}`}>
      <img
        src={src}
        alt={c.brand}
        className={`${sizes.img} object-contain object-center shrink-0 select-none`}
      />
      {showWordmark ? (
        <span className={`${sizes.text} font-bold tracking-wide leading-tight ${textClass}`}>{c.brand}</span>
      ) : (
        <span className="sr-only">{c.brand}</span>
      )}
    </span>
  );

  if (!href) return content;

  return (
    <Link href={href} className="inline-flex items-center hover:opacity-90 transition-opacity" aria-label={c.brand}>
      {content}
    </Link>
  );
}
